/**
 * Opt-out enforcement: when an inbound STOP/family keyword arrives, suppress
 * the contact, queue a single confirmation reply, and emit a ConsentEvent for
 * the audit trail.
 */

import { detectKeyword, type KeywordMatch } from "./keywords";
import { getDisclosure, renderDisclosure, type CampaignType, type Channel, type Language } from "./disclosures";

export interface OptOutInput {
  body: string;
  channel: Channel;
  fromE164: string;
  toE164: string;
  receivedAt: string;     // ISO
  campaignId?: string;
  organizationId: string;
  contactLanguage?: Language;
  campaignType?: CampaignType;
  clientName: string;
  supportEmail: string;
}

export interface OptOutResult {
  match: KeywordMatch;
  action:
    | "OPT_OUT"
    | "HELP_REPLY"
    | "OPT_IN_RESUME"
    | "NONE";
  replyText?: string;
  suppression?: {
    phoneE164: string;
    scope: "campaign" | "organization";
    reason: string;
    standard: boolean;
  };
  auditEvent: {
    type: "consent.revoked" | "help.requested" | "consent.restored" | "inbound.unmatched";
    at: string;
    contact: string;
    channel: Channel;
    body: string;
    matchedKeyword?: string;
    language: Language | "unknown";
    standard: boolean;
  };
}

export function handleInbound(input: OptOutInput): OptOutResult {
  const match = detectKeyword(input.body);
  const lang: Language =
    input.contactLanguage ?? (match.language === "es" ? "es" : "en");
  const campaignType: CampaignType = input.campaignType ?? "political";

  const disclosure = getDisclosure(campaignType, input.channel, lang);
  const rendered = disclosure
    ? renderDisclosure(disclosure, { clientName: input.clientName, supportEmail: input.supportEmail })
    : undefined;

  if (match.category === "STOP") {
    return {
      match,
      action: "OPT_OUT",
      replyText: rendered?.stopReply,
      suppression: {
        phoneE164: input.fromE164,
        // Standard STOP applies to campaign/sender pool per Twilio default;
        // explicit STOPALL or repeated revoke escalates to org. Keep conservative.
        scope: "campaign",
        reason: `Inbound ${match.matched} on ${input.channel}`,
        standard: match.standard,
      },
      auditEvent: {
        type: "consent.revoked",
        at: input.receivedAt,
        contact: input.fromE164,
        channel: input.channel,
        body: input.body,
        matchedKeyword: match.matched,
        language: match.language,
        standard: match.standard,
      },
    };
  }

  if (match.category === "HELP") {
    return {
      match,
      action: "HELP_REPLY",
      replyText: rendered?.helpReply,
      auditEvent: {
        type: "help.requested",
        at: input.receivedAt,
        contact: input.fromE164,
        channel: input.channel,
        body: input.body,
        matchedKeyword: match.matched,
        language: match.language,
        standard: match.standard,
      },
    };
  }

  if (match.category === "START") {
    return {
      match,
      action: "OPT_IN_RESUME",
      replyText: rendered?.optInConfirmation,
      auditEvent: {
        type: "consent.restored",
        at: input.receivedAt,
        contact: input.fromE164,
        channel: input.channel,
        body: input.body,
        matchedKeyword: match.matched,
        language: match.language,
        standard: match.standard,
      },
    };
  }

  return {
    match,
    action: "NONE",
    auditEvent: {
      type: "inbound.unmatched",
      at: input.receivedAt,
      contact: input.fromE164,
      channel: input.channel,
      body: input.body,
      language: match.language,
      standard: false,
    },
  };
}
